import {
	DateTextInput,
	Form,
	MaskedPhoneNumberInput,
	MaskedPhoneNumberInputSchema,
	SchemaBuilder,
	SelectInput,
	TextInput,
	useHookForm,
} from '@aspendental/shared-forms-web';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { type FieldValues } from 'react-hook-form';

import { GUARANTOR_RELATIONSHIPS } from '@src/constants';
import { GuarantorInformationSchema, useFormSectionStore, useGuarantorInformationFormStore } from '@src/store';
import { dateInvalid } from '@src/utils/date-parser';
import { unacceptableInputs } from '@src/utils/offensive-words';

const GuarantorInformationForm = ({ formId }: { formId: string }) => {
	const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
	const {
		firstName,
		lastName,
		dateOfBirth,
		zipCode,
		email,
		phoneNumber,
		relationshipToPatient,
		setFirstName,
		setLastName,
		setDateOfBirth,
		setZipCode,
		setEmail,
		setPhoneNumber,
		setRelationshipToPatient,
	} = useGuarantorInformationFormStore((store) => store);

	const completeActiveNode = useFormSectionStore((store) => store.completeActiveNode);
	const { t } = useTranslation('guarantorInfo');

	const schema = SchemaBuilder([
		{ name: 'firstName', validation: GuarantorInformationSchema.firstName },
		{ name: 'lastName', validation: GuarantorInformationSchema.lastName },
		{ name: 'dateOfBirth', validation: GuarantorInformationSchema.dateOfBirth },
		{ name: 'zipCode', validation: GuarantorInformationSchema.zipCode },
		{ name: 'email', validation: GuarantorInformationSchema.email },
		{ name: 'phoneNumber', validation: MaskedPhoneNumberInputSchema.options.default },
		{ name: 'relationshipToPatient', validation: GuarantorInformationSchema.relationshipToPatient },
	]);

	const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');

	const defaultValues = {
		firstName,
		lastName,
		dateOfBirth: initialValueDateOfBirth,
		zipCode,
		email,
		phoneNumber,
		relationshipToPatient,
	};

	const form = useHookForm({
		schema,
		defaultValues,
	});

	const handleSubmit = (fields: FieldValues) => {
		const unacceptableFields = unacceptableInputs([
			{ name: 'guarantorFirstName', value: fields.firstName },
			{ name: 'guarantorLastName', value: fields.lastName },
		]);

		if (unacceptableFields.length) {
			console.error('offensive inputs detected: ', unacceptableFields.join(', '));
			setOffensiveInputsFound(true);
			return;
		} else {
			setOffensiveInputsFound(false);
		}

		setFirstName(fields.firstName);
		setLastName(fields.lastName);
		setDateOfBirth(fields.dateOfBirth);
		setZipCode(fields.zipCode);
		setEmail(fields.email);
		setPhoneNumber(fields.phoneNumber.replace(/[^\d]/g, ''));
		setRelationshipToPatient(fields.relationshipToPatient);
		completeActiveNode();
	};

	return (
		<Form form={form} onSubmit={handleSubmit} formId={formId}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Alert severity="info" variant="flatSecondary">
						<Typography color="text.secondary">{t('parentGuardian')}</Typography>
					</Alert>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="firstName" label={t('firstNameLabel')} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="lastName" label={t('lastNameLabel')} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<DateTextInput name="dateOfBirth" label={t('dobLabel')} required />
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="email" label={t('emailLabel')} enforceAlphaNumeric={false} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<MaskedPhoneNumberInput name="phoneNumber" label={t('phoneNumLabel')} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<SelectInput
						name="relationshipToPatient"
						label={t('patientRelationship')}
						options={GUARANTOR_RELATIONSHIPS.map((option) => ({ label: option, value: option }))}
					/>
				</Grid>
				{offensiveInputsFound && (
					<Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveEntries')}</Typography>
						</Alert>
					</Grid>
				)}
			</Grid>
		</Form>
	);
};

export default GuarantorInformationForm;
